import classNames from 'classnames'
import React, { Suspense, useCallback, useEffect } from 'react'
import { Outlet, matchPath, useLocation } from 'react-router-dom'

import { useBrowserOutdated } from '@app/routing/hooks/useBrowserOutdated'
import { useLayoutHeight } from '@app/routing/hooks/useLayoutHeight'

import { CartDataFetcher } from '@pages/Subscriptions/hooks/useCartItemsData'

import { TourHost } from '@features/tours/TourHost'

import { useModals } from '@context/ModalsContext'
import { NavigationModeProvider } from '@context/NavigationModeContext'
import { useSidebar } from '@context/SidebarContext'

import { Modal } from '@ui/components/Modal'
import { SnackbarContainer } from '@ui/components/Snackbar/Snackbar'

import { PathHandler } from '@utils/PathHandler'
import { useWindowResize } from '@utils/domUtils'
import { closeHubSpotChat } from '@utils/hubspot'
import { CommonPaths } from '@utils/path-common'

import { ChatContactFab } from '@global/ChatContactFab/ChatContactFab'
import { ImpersonationBanner } from '@global/ImpersonationBanner/ImpersonationBanner'
import { StatusBanner } from '@global/StatusBanner/StatusBanner'
import { TickmillAppBanner } from '@global/TickmillAppBanner/TickmillAppBanner'
import { AppFooter } from '@global/footer/Footer'
import { MaintenanceMessage } from '@global/maintenanceMessage/MaintenanceMessage'
import { NotificationBlock } from '@global/notification/NotificationBlock'
import { PlatformNotification } from '@global/platformNotification/PlatformNotification'

import { useLiveChatWidget } from '@hooks/chats/useLiveChatWidget'
import { type FullLayoutConfig } from '@hooks/useLayoutVariant'

import { Loading } from '@shared/components/Loading/Loading'
import { MobileBottomBar } from '@shared/components/Navigation/v2/MobileNav/MobileBottomBar'
import { Sidebar } from '@shared/components/Navigation/v2/Sidebar/Sidebar'
import { TopBarFull } from '@shared/components/Navigation/v2/TopBar/TopBarFull'
import { PageNoticeContainer } from '@shared/components/PageNotice/PageNotice'

import styles from './LayoutFull.module.scss'

export const HIDE_CONTACT_FAB_PATTERNS = [`/:locale${CommonPaths.Markets}`]

interface LayoutProps {
  layout: FullLayoutConfig
  setCurrentPath: (path: string) => void
}

/**
 setting 3 — full layout
 * for WEB DESKTOP: WE HAVE sidebar + topBarFull + all banners/breadcrumbs/notifications/footer
 * for MOBILE: WE HAVE TopBarFull + content + MobileBottomBar (fixed bottom navigation)
 */
const LayoutFullInner: React.FC<LayoutProps> = ({ layout, setCurrentPath }) => {
  useBrowserOutdated()
  const location = useLocation()

  const { statusBannerRef, systemNotificationsRef, topBarRef } = useLayoutHeight()
  const { modals } = useModals()
  const isMobile = useWindowResize()
  const { closeSubmenu } = useSidebar()
  const handleContentMouseEnter = useCallback(() => closeSubmenu(), [closeSubmenu])
  const isFlushContent = layout.content === 'flush'
  const isContainerContent = layout.content === 'container'
  const isSemiContent = layout.content === 'semi'
  const isMenuVisible = layout.menu === 'visible'
  const isTopbarVisible = layout.topbar === 'visible'
  const shouldRenderShellNotices = layout.notices === 'visible'
  const mainClassName = classNames(styles.main, {
    [styles.mainMenuVisible]: isMenuVisible,
    [styles.mainPadded]: !isFlushContent,
    [styles.mainFlush]: isFlushContent,
    [styles.mainInnerSemi]: isSemiContent,
  })
  const mainInnerClassName = classNames(styles.mainInner, {
    [styles.mainInnerContainer]: isContainerContent,
  })
  const hideContactFAB =
    !isMobile && HIDE_CONTACT_FAB_PATTERNS.some((pattern) => matchPath(pattern, location.pathname))
  const { closeLiveChat } = useLiveChatWidget()
  useEffect(() => {
    if (hideContactFAB) {
      closeLiveChat()
      void closeHubSpotChat()
    }
  }, [closeLiveChat, hideContactFAB])

  return (
    <div className='expander defaultBackground' id='mainWrapper'>
      <ImpersonationBanner />
      {!hideContactFAB && <ChatContactFab />}
      <CartDataFetcher />
      <SnackbarContainer />
      <Modal.Wrapper modals={modals} />

      {isMobile ? (
        // -- MOBILE --
        <>
          {shouldRenderShellNotices && (
            <div id='app-header'>
              <div id='app-banners' ref={systemNotificationsRef}>
                <MaintenanceMessage />
                <PlatformNotification />
                <TickmillAppBanner />
              </div>
            </div>
          )}

          <main className={mainClassName} onMouseEnter={handleContentMouseEnter}>
            <div className={mainInnerClassName}>
              <Suspense fallback={<Loading isLoading showLoadingIcon />}>
                {shouldRenderShellNotices && (
                  <>
                    <NotificationBlock />
                    <div ref={statusBannerRef}>
                      <StatusBanner />
                    </div>
                    <TourHost />
                    <PageNoticeContainer />
                  </>
                )}
                <PathHandler setCurrentPath={setCurrentPath} />
                <div className={styles.outlet}>
                  <Outlet />
                </div>
              </Suspense>
            </div>
          </main>

          {isMenuVisible && <MobileBottomBar />}
        </>
      ) : (
        // -- DESKTOP --
        <div className={styles.root}>
          {shouldRenderShellNotices && (
            <div ref={systemNotificationsRef}>
              <MaintenanceMessage />
              <PlatformNotification />
            </div>
          )}
          {isTopbarVisible && (
            <div ref={topBarRef}>
              <TopBarFull />
            </div>
          )}
          <div className={styles.body}>
            {isMenuVisible && <Sidebar />}
            <main onMouseEnter={handleContentMouseEnter} className={mainClassName}>
              <div className={mainInnerClassName}>
                <Suspense fallback={<Loading isLoading showLoadingIcon />}>
                  {shouldRenderShellNotices && (
                    <>
                      <NotificationBlock />
                      <div ref={statusBannerRef}>
                        <StatusBanner />
                      </div>
                      <TourHost />
                      <PageNoticeContainer />
                    </>
                  )}
                  <PathHandler setCurrentPath={setCurrentPath} />
                  <div className={styles.outlet}>
                    <Outlet />
                  </div>
                </Suspense>
              </div>
            </main>
            <AppFooter />
          </div>
        </div>
      )}
    </div>
  )
}

export const LayoutFull: React.FC<LayoutProps> = (props) => (
  <NavigationModeProvider>
    <LayoutFullInner {...props} />
  </NavigationModeProvider>
)
